import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import CandidateApp from "./prototype/CandidateApp";
import EmployerApp from "./prototype/EmployerApp";
import AdminApp from "./prototype/AdminApp";
import PrintDeck from "./prototype/PrintDeck";
import { Logo, Icon, Chip } from "./prototype/ui";

function Landing() {
  const nav = useNavigate();
  const cards = [
    { k: "candidate", tag: "Mobile · React Native", title: "Candidate app",
      sub: "For job-seekers on notice period or immediately available.",
      screens: "12 screens", colors: "linear-gradient(135deg, #0F3D2E 0%, #14B87A 100%)",
      testid: "pick-candidate", accent: "#14B87A" },
    { k: "employer", tag: "Web · React", title: "Employer panel",
      sub: "For TA leads posting urgent openings — Kanban, filters, unlocks.",
      screens: "10+ views · Kanban board", colors: "linear-gradient(135deg, #0B0F14 0%, #253143 100%)",
      testid: "pick-employer", accent: "#FF6A3D" },
    { k: "admin", tag: "Web · Internal", title: "Super admin",
      sub: "For TalentXpres ops — verify, moderate, monitor MRR.",
      screens: "7 sections", colors: "linear-gradient(135deg, #253143 0%, #0F3D2E 100%)",
      testid: "pick-admin", accent: "#B7F0C5" },
  ];

  return (
    <div className="min-h-screen relative grain overflow-hidden" style={{background: "var(--paper)"}}>
      <div className="relative z-10">
        <header className="max-w-7xl mx-auto flex items-center justify-between py-6 px-6">
          <Logo/>
          <div className="flex items-center gap-3">
            <a href="/TalentXpres-Prototype-v1.pdf" download data-testid="download-pdf" className="btn btn-primary text-sm hidden sm:inline-flex">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v12M6 9l6 6 6-6M4 21h16"/></svg>
              Download PDF
            </a>
            <Link to="/print" data-testid="export-pdf" className="btn btn-ghost text-sm hidden md:inline-flex">View walkthrough</Link>
            <Link to="/demo" data-testid="open-demo" className="btn btn-ghost text-sm">Demo mode →</Link>
            <Chip tone="mint">Prototype v1</Chip> 
          </div>
        </header>

        <section className="max-w-7xl mx-auto px-6 pt-12 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-widest text-[var(--brand)]">MVP v1 · Interactive prototype</div>
              <h1 className="font-display text-6xl lg:text-8xl leading-[0.95] mt-4">
                Hire people who<br/>can <span className="text-[var(--brand-2)]">actually</span> join.
              </h1>
              <p className="mt-6 text-lg text-[var(--ink-2)] max-w-2xl leading-relaxed">
                TalentXpres matches urgent openings with candidates serving notice period or ready immediately. This clickable prototype walks through all three product surfaces — pick one to explore.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="card p-5">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">The rule that makes it work</div>
                <div className="font-display text-2xl mt-2 leading-tight">Both sides declare a 0–90 day availability window. We only match when they overlap.</div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="avail-chip avail-green"><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/> Immediate</div>
                  <div className="avail-chip avail-yellow"><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/> ≤45d</div>
                  <div className="avail-chip avail-orange"><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/> ≤90d</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-5">
            {cards.map(c => (
              <button key={c.k} data-testid={c.testid} onClick={() => nav(`/${c.k}`)}
                className="text-left group relative overflow-hidden rounded-2xl p-8 min-h-[280px] flex flex-col justify-between transition"
                style={{background: c.colors, color: "white"}}>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">{c.tag}</div>
                  <div className="font-display text-3xl mt-3 leading-tight">{c.title}</div>
                  <p className="text-sm mt-2 opacity-80 max-w-xs">{c.sub}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">{c.screens}</span>
                  <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center backdrop-blur group-hover:bg-white group-hover:text-[var(--ink)] transition"><Icon.Arrow/></span>
                </div>
                <div className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full opacity-30" style={{background: c.accent}}/>
              </button>
            ))}
          </div>

          <div className="mt-20 grid md:grid-cols-4 gap-6">
            {[
              {n:"01", t:"Availability first", d:"Candidates declare notice-end date. Employers declare max joining timeline. No mismatch, ever."},
              {n:"02", t:"Weighted matching", d:"Availability 30% · Skills 25% · Role 15% · Experience 12% · Location 10% · Salary 8%."},
              {n:"03", t:"Trust-verified", d:"Every employer manually verified via GST/CIN. Green badge = green light."},
              {n:"04", t:"Kanban-native", d:"Applications flow through New → Interview → Offer → Hired without drop-offs."},
            ].map(x => (
              <div key={x.n} className="border-t border-[var(--line)] pt-5">
                <div className="font-mono text-xs text-[var(--muted)]">{x.n}</div>
                <div className="font-display text-xl mt-2">{x.t}</div>
                <p className="text-sm text-[var(--ink-2)] mt-2">{x.d}</p>
              </div>
            ))}
          </div>

          {/* Pricing snapshot */}
          <div className="mt-20 pt-10 border-t border-[var(--line)]">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Build estimate</div>
                <h2 className="font-display text-4xl mt-2">Investment & timeline</h2>
              </div>
              <a href="/TalentXpres-Prototype-v1.pdf" download className="text-sm text-[var(--brand)] hover:underline">Full breakdown in PDF →</a>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Total build</div>
                <div className="font-display text-4xl mt-2">₹31–43L</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">One-time · MVP v1</div>
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Timeline</div>
                <div className="font-display text-4xl mt-2">20 wks</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">Design → Beta → Launch</div>
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Team size</div>
                <div className="font-display text-4xl mt-2">6.5 FTE</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">PM · Design · FE · Mobile · BE · QA · DevOps</div>
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Monthly infra</div>
                <div className="font-display text-4xl mt-2">₹28–60K</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">Post-launch, small scale</div>
              </div>
            </div>

            <div className="mt-6 card p-6">
              <h3 className="font-display text-2xl mb-4">Build cost breakdown</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {[
                    {l:"Design (Figma) · 4–6 weeks", v:"₹3.0–4.5L", w:12},
                    {l:"Backend (Node + Postgres)", v:"₹10.0–14.0L", w:33},
                    {l:"Web frontend (Employer + Admin)", v:"₹6.0–8.0L", w:19},
                    {l:"Mobile app (Candidate · React Native)", v:"₹7.0–9.0L", w:22},
                  ].map(r => (
                    <div key={r.l}>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-[var(--ink-2)]">{r.l}</span>
                        <span className="font-mono font-semibold">{r.v}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-[var(--paper-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand)] rounded-full" style={{width:`${r.w*2.4}%`}}/>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  {[
                    {l:"QA + UAT", v:"₹1.5–2.5L", w:6},
                    {l:"DevOps + infra setup", v:"₹1.0–1.5L", w:4},
                    {l:"PM + project overhead", v:"₹2.5–3.5L", w:8},
                  ].map(r => (
                    <div key={r.l}>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-[var(--ink-2)]">{r.l}</span>
                        <span className="font-mono font-semibold">{r.v}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-[var(--paper-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand-2)] rounded-full" style={{width:`${r.w*2.4}%`}}/>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 p-3 rounded-xl bg-[var(--brand-3)]/40 border border-[var(--brand-2)]/30">
                    <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Recommended</div>
                    <div className="font-display text-2xl mt-1">₹37,00,000 <span className="text-sm text-[var(--muted)] font-sans font-normal">· mid-point blended team</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="font-display text-xl mb-3">Team composition</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ["Product Manager", "1 · 20 wks"],
                    ["UI/UX Designer (Figma)", "1 · heavy wks 1-6, part-time after"],
                    ["Frontend Engineer (React web)", "1 · 16 wks"],
                    ["Mobile Engineer (React Native)", "1 · 16 wks"],
                    ["Backend Engineer (Node + Postgres)", "2 · 16 wks"],
                    ["QA Engineer", "1 · 9 wks"],
                    ["DevOps (part-time)", "0.5 · 16 wks"],
                  ].map(([r, d]) => (
                    <div key={r} className="flex justify-between border-b border-dashed border-[var(--line)] pb-2 last:border-0">
                      <span>{r}</span><span className="text-[var(--muted)] font-mono text-xs">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card p-6">
                <h3 className="font-display text-xl mb-3">Monthly running costs</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ["Cloud hosting (containers)", "₹15–30K"],
                    ["Managed database (Postgres/Atlas)", "₹8–15K"],
                    ["SendGrid (transactional email)", "₹2–5K"],
                    ["Object storage + CDN", "₹3–8K"],
                    ["Razorpay", "2% per txn"],
                    ["FCM push notifications", "Free"],
                  ].map(([n, v]) => (
                    <div key={n} className="flex justify-between border-b border-dashed border-[var(--line)] pb-2 last:border-0">
                      <span>{n}</span><span className="font-mono text-xs">{v}</span>
                    </div>
                  ))}
                  <div className="mt-3 pt-3 border-t border-[var(--line)] flex justify-between font-semibold">
                    <span>Total infra / month</span>
                    <span className="font-mono">₹28–60K</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="border-t border-[var(--line)] py-6 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-[var(--muted)]">
            <div>© 2026 TalentXpres · Prototype for client review</div>
            <div className="font-mono">3 apps · 29 screens · India MVP</div>
          </div>
        </footer>
      </div>
    </div>
  );
}

/* Minimal 3-button demo page at /demo */
function DemoLanding() {
  const nav = useNavigate();
  const buttons = [
    { k: "candidate", label: "Candidate", sub: "Mobile app · 12 screens",
      colors: "linear-gradient(135deg, #0F3D2E 0%, #14B87A 100%)", testid: "demo-candidate" },
    { k: "employer", label: "Employer", sub: "Web console · Kanban + billing",
      colors: "linear-gradient(135deg, #0B0F14 0%, #253143 100%)", testid: "demo-employer" },
    { k: "admin", label: "Super Admin", sub: "Ops · verify, moderate, monitor",
      colors: "linear-gradient(135deg, #253143 0%, #0F3D2E 100%)", testid: "demo-admin" },
  ];
  return (
    <div className="min-h-screen grain relative flex flex-col" style={{background: "var(--paper)"}}>
      <header className="max-w-7xl w-full mx-auto flex items-center justify-between py-6 px-6 relative z-10">
        <Logo/>
       {/* <Link to="/" className="btn btn-ghost text-sm">Full prototype →</Link>*/}
      </header>
      <main className="flex-1 flex items-center justify-center relative z-10 px-6 pb-16">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">TalentXpres · Live demo</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.02] mt-4">Pick a role to demo.</h1>
            <p className="text-[var(--ink-2)] mt-4 max-w-lg mx-auto">Each button opens the full working experience for that user — every feature, every screen.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {buttons.map(b => (
              <button key={b.k} data-testid={b.testid} onClick={() => nav(`/demo/${b.k}`)}
                className="text-left group relative overflow-hidden rounded-3xl p-8 min-h-[220px] flex flex-col justify-between transition hover:scale-[1.02]"
                style={{background: b.colors, color: "white"}}>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">Open</div>
                  <div className="font-display text-4xl mt-3">{b.label}</div>
                  <p className="text-sm mt-2 opacity-80">{b.sub}</p>
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <span className="text-sm font-semibold">Enter</span>
                  <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center backdrop-blur group-hover:bg-white group-hover:text-[var(--ink)] transition"><Icon.Arrow/></span>
                </div>
              </button>
            ))}
          </div>
        </div>

        
      </main>
      <div className="mt-20 pt-10 border-t border-[var(--line)] m-20 ">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Build estimate</div>
                <h2 className="font-display text-4xl mt-2">Investment & timeline</h2>
              </div>
              {/*<a href="/TalentXpres-Prototype-v1.pdf" download className="text-sm text-[var(--brand)] hover:underline">Full breakdown in PDF →</a>*/}
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Total build</div>
                <div className="font-display text-4xl mt-2">₹1–2L</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">One-time · MVP v1</div>
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Timeline</div>
                <div className="font-display text-4xl mt-2">14 wks</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">Design → Beta → Launch</div>
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Team size</div>
                <div className="font-display text-4xl mt-2">4</div>
                {/*<div className="text-xs text-[var(--ink-2)] mt-1">PM · Design · FE · Mobile · BE · QA · DevOps</div>*/}
              </div>
              <div className="card p-6">
                <div className="text-xs uppercase tracking-widest text-[var(--muted)]">Monthly Maintenance</div>
                <div className="font-display text-4xl mt-2">₹8–15K</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">Post-launch, small scale</div>
              </div>
            </div>

            <div className="mt-6 card p-6">
              <h3 className="font-display text-2xl mb-4">Build cost breakdown</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {[
                    {l:"Design (Figma) · 1–2 weeks", v:"₹25000", w:12},
                    {l:"Backend (Node + Postgres) . 2–3 weeks", v:"₹45000", w:33},
                    {l:"Web frontend (Employer + Admin) . 3 weeks", v:"₹40000", w:19},
                    {l:"Mobile app (Candidate · React Native) . 3 weeks", v:"₹45000", w:22},
                  ].map(r => (
                    <div key={r.l}>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-[var(--ink-2)]">{r.l}</span>
                        <span className="font-mono font-semibold">{r.v}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-[var(--paper-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand)] rounded-full" style={{width:`${r.w*2.4}%`}}/>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  {[
                    {l:"QA + UAT", v:"₹25000", w:6},
                    {l:"DevOps + infra setup", v:"₹15000", w:4},
                    {/*l:"PM + project overhead", v:"₹2.5–3.5L", w:8*/},
                  ].map(r => (
                    <div key={r.l}>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-[var(--ink-2)]">{r.l}</span>
                        <span className="font-mono font-semibold">{r.v}</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-[var(--paper-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand-2)] rounded-full" style={{width:`${r.w*2.4}%`}}/>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 p-3 rounded-xl bg-[var(--brand-3)]/40 border border-[var(--brand-2)]/30">
                    <div className="text-xs uppercase tracking-widest text-[var(--brand)]">Recommended</div>
                    <div className="font-display text-2xl mt-1">₹1,95,000 <span className="text-sm text-[var(--muted)] font-sans font-normal">· mid-point blended team</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              {/*<div className="card p-6">
                <h3 className="font-display text-xl mb-3">Team composition</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ["Product Manager", "1 · 20 wks"],
                    ["UI/UX Designer (Figma)", "1 · heavy wks 1-6, part-time after"],
                    ["Frontend Engineer (React web)", "1 · 16 wks"],
                    ["Mobile Engineer (React Native)", "1 · 16 wks"],
                    ["Backend Engineer (Node + Postgres)", "2 · 16 wks"],
                    ["QA Engineer", "1 · 9 wks"],
                    ["DevOps (part-time)", "0.5 · 16 wks"],
                  ].map(([r, d]) => (
                    <div key={r} className="flex justify-between border-b border-dashed border-[var(--line)] pb-2 last:border-0">
                      <span>{r}</span><span className="text-[var(--muted)] font-mono text-xs">{d}</span>
                    </div>
                  ))}
                </div>
              </div>*/}
              <div className="card p-6">
                <h3 className="font-display text-xl mb-3">Monthly running costs</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ["Cloud hosting (containers)", "₹3–5K"],
                    ["Managed database (Postgres/Atlas)", "₹3-5K"],
                    ["SendGrid (transactional email)", "₹2–5K"],
                    ["Razorpay", "2% per txn"],
                    ["FCM push notifications", "Free"],
                  ].map(([n, v]) => (
                    <div key={n} className="flex justify-between border-b border-dashed border-[var(--line)] pb-2 last:border-0">
                      <span>{n}</span><span className="font-mono text-xs">{v}</span>
                    </div>
                  ))}
                  <div className="mt-3 pt-3 border-t border-[var(--line)] flex justify-between font-semibold">
                    <span>Total infra / month</span>
                    <span className="font-mono">₹8–15K</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
      <footer className="text-xs text-[var(--muted)] text-center pb-6 relative z-10 font-mono">talentxpres.com · v1 demo</footer>
    </div>
  );
}

function DemoWrap({ children }) {
  const nav = useNavigate();
  return (
    <div className="relative">
      <button onClick={() => nav("/demo")} data-testid="demo-back"
        className="fixed top-4 right-4 z-[60] btn text-sm shadow-lg"
        style={{background:"var(--ink)", color:"var(--paper)"}}>
        ← Back to menu
      </button>
      {children}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/*<Route path="/" element={<Landing/>}/>*/}
        <Route path="/" element={<DemoLanding/>}/>
        <Route path="/candidate" element={<CandidateApp onExit={() => window.location.assign("/")}/>}/>
        <Route path="/employer" element={<EmployerApp onExit={() => window.location.assign("/")}/>}/>
        <Route path="/admin" element={<AdminApp onExit={() => window.location.assign("/")}/>}/>
        <Route path="/print" element={<PrintDeck onExit={() => window.location.assign("/")}/>}/>
        <Route path="/demo" element={<DemoLanding/>}/>
        <Route path="/demo/candidate" element={<DemoWrap><CandidateApp onExit={() => window.location.assign("/demo")} hideNav/></DemoWrap>}/>
        <Route path="/demo/employer" element={<DemoWrap><EmployerApp onExit={() => window.location.assign("/demo")}/></DemoWrap>}/>
        <Route path="/demo/admin" element={<DemoWrap><AdminApp onExit={() => window.location.assign("/demo")}/></DemoWrap>}/>
      </Routes>
    </BrowserRouter>
  );
}
